import {
  EDIT_ACCOUNT_INFORMATION_SUCCESS,
  EDIT_ACCOUNT_INFORMATION_TITLE
} from '@bo-constants/member/memberCopy';
import { useCallback, useMemo } from 'react';
import type { FieldValues } from 'react-hook-form';
import { Form } from '@library/components/forms';
import type { MemberAccountInformation } from '@library/types/members';
import { MemberAccountInformationFormFields } from '@bo-components/members/forms';
import { MemberConfirmationModal } from '@bo-components/members/modal';
import { classNames } from '@library/utils';
import { getDirtyFieldsValues } from '@bo-utils/forms';
import { memberAccountInfoSchema } from '@library/types/members/schemas/memberAccountInfoSchema';
import { useForm } from 'react-hook-form';
import { useMemberAccountInformation } from '@bo-hooks/members';
import { zodResolver } from '@hookform/resolvers/zod';

export interface MemberEditAccountInformationModalProps {
  accountInformation: MemberAccountInformation;
  memberId: string;
  onClose: () => void;
}

export const MemberEditAccountInformationModal = ({
  accountInformation,
  memberId,
  onClose
}: MemberEditAccountInformationModalProps) => {
  const {
    errorMessage,
    isError,
    isLoading,
    isSuccess,
    isUninitialized,
    onSubmit
  } = useMemberAccountInformation({ memberId });

  const form = useForm({
    resolver: zodResolver(memberAccountInfoSchema),
    defaultValues: accountInformation as FieldValues
  });
  const {
    formState: { dirtyFields },
    handleSubmit
  } = form;

  // Confirmation message
  const confirmationMessage = useMemo(() => {
    if (isSuccess) {
      return EDIT_ACCOUNT_INFORMATION_SUCCESS;
    }
    if (isError) {
      return errorMessage;
    }
    return undefined;
  }, [errorMessage, isError, isSuccess]);

  // Modal visibility & button states
  const isAnyFieldDirty = Object.keys(dirtyFields).length > 0;
  const isFormVisible = isUninitialized || isLoading;
  const isConfirmDisabled = isLoading || !isAnyFieldDirty;

  const handleFormSubmit = useCallback(async () => {
    const updatedFields = getDirtyFieldsValues(dirtyFields, form);
    await onSubmit(updatedFields);
  }, [dirtyFields, form, onSubmit]);

  return (
    <MemberConfirmationModal
      cancelCopy="Cancel"
      childrenClassName="justify-evenly py-4 min-h-32"
      className="w-[500px]"
      confirmationCopy="Save"
      handleConfirm={handleSubmit(handleFormSubmit)}
      isButtonDisabled={isLoading}
      isConfirmDisabled={isConfirmDisabled}
      isOpen
      label={EDIT_ACCOUNT_INFORMATION_TITLE}
      onCancelModal={onClose}
      showConfirmationMessage={!isFormVisible}
    >
      {isFormVisible ? (
        <Form
          autoComplete="off" // Prevent browser from auto-filling the form
          className="flex size-full flex-wrap gap-4"
          form={form}
          onSubmit={handleFormSubmit}
        >
          <MemberAccountInformationFormFields />
        </Form>
      ) : (
        <p
          className={classNames(
            'text-sm',
            isError ? 'text-roses' : 'text-soClean-180'
          )}
        >
          {confirmationMessage}
        </p>
      )}
    </MemberConfirmationModal>
  );
};
